import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  IRoleWithPermissionsFieldsFragment,
  IUserEditMutation,
  IUserEditMutationVariables,
  IUserGroupFieldsFragment,
  IUserPermissionFieldsFragment,
  IUserPermissionQuery,
  IUserPermissionQueryVariables,
  PermissionKey,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import { batch } from 'react-redux';
import { setDictionaryListAction } from '../../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../../utils/interfaces';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_SINGLE_USER_PERMISSION,
  USER_EDIT,
} from '../../../../utils/apollo/permission';
import { appRoutes } from '../../../../path';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../../components/PageHeader';
import * as PermissionControlsUI from '../../../PermissionControls/PermissionControlsUI';
import styled from 'styled-components';
import GroupAssignTable from '../../UserGroups/GroupAssignTable';
import RoleAssignTable from '../../Roles/RoleAssignTable';
import PermissionElementWrapper from '../../../PermissionControls/PermissionElementWrapper';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import toastMessage from '../../../../utils/toastMessage';
import { ContentLoader } from '../../../../components/Loaders';
import UserProfileCard from '../components/UserProfileCard';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../../utils/analytics/events/page/events';
import ThinDivider from '../../../../components/ThinDivider';

const Footer = styled.div`
  display: flex;
  column-gap: 16px;
  align-items: center;
`;

/**
 * UserModify is a page that allows the user to edit a user's groups and roles
 */
const UserModify = () => {
  const { checkIsPermitted } = usePermission();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminEdit],
  });
  const urlParams = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userId } = urlParams;
  assertNotNil(userId);

  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: 'edit user',
        id: userId,
      }),
    );
  }, [track, userId]);

  const selectedRoles = useAppSelector(
    (state) => state.dictionary.roles,
  ) as IRoleWithPermissionsFieldsFragment[];
  const selectedUserGroups = useAppSelector(
    (state) => state.dictionary.userGroups,
  ) as IUserGroupFieldsFragment[];
  const [user, setUser] = useState<IUserPermissionFieldsFragment | null>(null);
  const onFetchUser = ({ user }: IUserPermissionQuery) => {
    setUser(user);
    batch(() => {
      dispatch(
        setDictionaryListAction({
          newValues: user.roles,
          dictionaryType: DictionaryTypeEnum.Roles,
        }),
      );
      dispatch(
        setDictionaryListAction({
          newValues: user.groups,
          dictionaryType: DictionaryTypeEnum.UserGroups,
        }),
      );
    });
  };
  const { refetch, loading: userLoading } = useQuery<
    IUserPermissionQuery,
    IUserPermissionQueryVariables
  >(GET_SINGLE_USER_PERMISSION, {
    variables: {
      id: userId,
    },
    onCompleted: onFetchUser,
  });

  const navigateToUser = () => {
    assertNotNil(user);
    navigate(`${appRoutes.permission.users}/${user.id}`);
  };

  const userRoleIds = user?.roles.map((role) => role.id) ?? [];
  const userGroupIds = user?.groups.map((group) => group.id) ?? [];
  const titleList = user
    ? [
        { name: 'Users', path: appRoutes.permission.users },
        { name: user.name, path: appRoutes.permission.users + '/' + user.id },
        { name: 'Edit Permissions', path: '' },
      ]
    : [];

  const [updateUser, { loading }] = useMutation<
    IUserEditMutation,
    IUserEditMutationVariables
  >(USER_EDIT);
  const onUpdateUser = async () => {
    assertNotNil(user);
    const { data } = await updateUser({
      variables: {
        input: {
          userId: user.id,
          roleIds: selectedRoles.map((role) => role.id),
          groupIds: selectedUserGroups.map((userGroup) => userGroup.id),
        },
      },
    });
    assertNotNil(data?.userEdit);
    if (printGraphqlErrors(data.userEdit.errors)) {
      return;
    }
    await refetch();
    toastMessage({ message: 'Permissions have been updated.', type: 'info' });
    navigateToUser();
  };

  // reset dictionary list on unmount. Right now all forms are using the same dictionary list from redux
  // so we need to reset it. We planned to remove dictionarySlice and use state only for dictionary list.
  useEffect(() => {
    return () => {
      batch(() => {
        dispatch(
          setDictionaryListAction({
            newValues: [],
            dictionaryType: DictionaryTypeEnum.UserGroups,
          }),
        );
        dispatch(
          setDictionaryListAction({
            newValues: [],
            dictionaryType: DictionaryTypeEnum.Roles,
          }),
        );
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (userLoading) {
    return <ContentLoader />;
  }
  return (
    <>
      <ClickablePageTitle titleList={titleList} />
      <PageHeaderDivider />
      {user && (
        <PermissionControlsUI.OverviewContainer>
          <UserProfileCard user={user} isEditing={true} />
          <ThinDivider layout="horizontal" size="full" />
          <GroupAssignTable userGroupIds={userGroupIds} />
          <RoleAssignTable userRoleIds={userRoleIds} />
          <Footer>
            <PermissionElementWrapper isPermitted={isPermittedToEdit}>
              <PrimaryButton
                onClick={onUpdateUser}
                loading={loading}
                disabled={!isPermittedToEdit}
                trackContext="users"
                trackAction="save"
              >
                Save
              </PrimaryButton>
            </PermissionElementWrapper>
            <SecondaryButton
              onClick={navigateToUser}
              trackContext="users"
              trackAction="cancel"
            >
              Cancel
            </SecondaryButton>
          </Footer>
        </PermissionControlsUI.OverviewContainer>
      )}
    </>
  );
};

export default UserModify;
